import { debounceTime, map, catchError } from 'rxjs/operators';
import { IMember, IMemberResult } from './../../models/member';
import { Injectable, OnInit } from '@angular/core';
import { DataService } from '../data-services/data.service';
import { Observable, of } from 'rxjs';
import { IOrganization } from '../../models/organization';
import { ILoginInformation } from '../../models/login-information';
import { ITag } from '../../models/tag';

@Injectable({
  providedIn: 'root',
})
export class MemberService
  extends DataService<IMemberResult>
  implements OnInit
{
  protected collectionname = 'users';
  selectedMember: IMemberResult;
  public organizationId: string;
  public currentOrganization: IOrganization;
  public organizations: Array<IOrganization>;

  loginUser: ILoginInformation;

  private loginCache = new Map<string, Observable<ILoginInformation>>();
  getLoginInformation(email: string): Observable<ILoginInformation> {
    if (this.loginCache.has(email)) {
      return this.loginCache.get(email)!;
    }

    const url = `${this.urlService.buildUrl(
      this.collectionname,
      'getLoginInformation'
    )}/${email}`;
    const response$ = this.http.get<ILoginInformation>(`${url}`);
    this.loginCache.set(email, response$);
    return response$;
  }

  getMembersByOrganization(
    organizationId: string,
    hideInactive = true
  ): Observable<Array<IMemberResult>> {
    // console.log('term', term);
    const url = hideInactive
      ? `${this.urlService.buildUrl(
          this.collectionname,
          'userByOrganization'
        )}/${organizationId}`
      : `${this.urlService.buildUrl(
          this.collectionname,
          'userByOrganization'
        )}/${organizationId}/showInactive/true`;
    return this.http.get<Array<IMemberResult>>(`${url}`).pipe(
      map((res: Array<IMemberResult>) => {
        res.forEach((item) => {
          this.setId(item);

          let organization = item.organizations.find(
            (i) => i.organizationId === organizationId
          );
          item['organizationId'] = organization.organizationId;
          item['organizationStatus'] = organization['status'];
          item['status'] = organization['status'];
          item['type'] = organization['type'] || 'member';
          item['isFinancial'] = organization['isFinancial'];
          item['financialStatusUpdatedDate'] = organization['financialStatusUpdatedDate'];
        });

        return res || ({} as Array<IMemberResult>);
      }),
      catchError(this.errorMgmt)
    );
  }

  getVendorsByOrganization(
    organizationId: string,
    hideInactive = true
  ): Observable<Array<IMemberResult>> {
    // console.log('term', term);
    const url = `${this.urlService.buildUrl(
      this.collectionname,
      ''
    )}/${organizationId}/vendors`;

    return this.http.get<Array<IMemberResult>>(`${url}`).pipe(
      map((res: Array<IMemberResult>) => {
        res.forEach((item) => {
          this.setId(item);

          let organization = item.organizations.find(
            (i) => i.organizationId === organizationId
          );
          item['organizationId'] = organization.organizationId;
          item['organizationStatus'] = organization['status'];
          item['status'] = organization['status'];
          item['name'] = item.lastname
            ? item.lastname + ', ' + item.firstname
            : item.firstname;
        });

        return res || ({} as Array<IMemberResult>);
      }),
      catchError(this.errorMgmt)
    );
  }

  getMembersByOrganizationAndType(
    organizationId: string,
    type: string,
    hideInactive = true
  ): Observable<Array<IMemberResult>> {
    // console.log('term', term);
    const url = `${this.urlService.buildUrl(
      this.collectionname,
      'userByOrganization'
    )}/${organizationId}/user-type/${type}/showInactive/${!hideInactive}`;

    return this.http.get<Array<IMemberResult>>(`${url}`).pipe(
      map((res: Array<IMemberResult>) => {
        res.forEach((item) => {
          this.setId(item);

          let organization = item.organizations.find(
            (i) => i.organizationId === organizationId
          );
          const attributes = organization.attributes;
          item['organizationId'] = organization.organizationId;
          item['attributes'] = attributes;
          item['organizationStatus'] = organization['status'];
          item['status'] = organization['status'];
          item['isFinancial'] = organization['isFinancial'];
          item['financialStatusUpdatedDate'] = organization['financialStatusUpdatedDate'];
          if (attributes && Object.keys(attributes).length) {
            Object.keys(attributes).forEach((key) => {
              const newKey = key
                .toLowerCase()
                .replace(/ /g, '_')
                .replace(/,/g, '');

              item[newKey] = attributes[key];
            });
          }
        });

        return res || ({} as Array<IMemberResult>);
      }),
      catchError(this.errorMgmt)
    );
  }
  getNotificationUsers(
    organizationId: string
  ): Observable<Array<IMemberResult>> {
    // console.log('term', term);
    const url = `${this.urlService.buildUrl(
      this.collectionname,
      organizationId
    )}/notification-users`;

    return this.http.get<Array<IMemberResult>>(`${url}`).pipe(
      map((res: Array<IMemberResult>) => {
        res.forEach((item) => {
          this.setId(item);
          item['fullname'] = `${item.firstname + ' ' + item.lastname}`;
        });

        return res || ({} as Array<IMemberResult>);
      }),
      catchError(this.errorMgmt)
    );
  }
  findUsers(
    organizationId: string,
    type: string,
    term = ''
  ): Observable<Array<IMemberResult>> {
    // console.log('term', term);
    const url = `${this.urlService.buildUrl(
      this.collectionname,
      'findUser'
    )}/${organizationId}/type/${type}/search/${term}`;
    return this.http.get<Array<IMemberResult>>(`${url}`);
  }

  public removeUserFromOrganization(user: IMember): Observable<IMember> {
    const url = `${this.urlService.buildUrl(
      this.collectionname,
      'removeUser'
    )}/${user.id}`;
    return this.http
      .patch<IMember>(url, user, { headers: this.headers })
      .pipe(catchError(this.errorMgmt));
  }

  public updateProfileImage() {}

  ngOnInit(): void {
    this.setCollectionName('users');
  }

  updatePreference(preference: string, value: string) {
    const url = `${this.urlService.buildUrl(this.collectionname, 'update')}/${
      this.user.id
    }`;
    const updateValue = {
      [preference]: value,
    };
    return this.http
      .patch<IMember>(url, updateValue, { headers: this.headers })
      .pipe(catchError(this.errorMgmt));
  }

  updateStatus(userId: string, status: boolean): Observable<IMember> {
    const url = `${this.urlService.buildUrl(
      this.collectionname,
      'status'
    )}/${userId}`;
    const updateValue = {
      organizationId: this.organization.organizationId,
      userId,
      status,
    };
    return this.http
      .patch<IMember>(url, updateValue, { headers: this.headers })
      .pipe(catchError(this.errorMgmt));
  }

  updateMemberTags(
    memberIds: string[],
    tag: ITag // Should this be the tag
  ): Observable<IMember[]> {
    const url = this.urlService.buildUrl(
      this.collectionname,
      tag.id,
      'assign-tag'
    );
    let data = {
      memberIds: memberIds,
      tag: tag,
    };
    return this.http
      .patch<IMember[]>(url, data, { headers: this.headers })
      .pipe(catchError(this.errorMgmt));
  }

  bulkAssignRole(
    organizationId: string,
    memberIds: string[],
    role: string
  ): Observable<IMember[]> {
    const url = this.urlService.buildUrl(
      this.collectionname,
      'bulk-assign-role'
    );
    const data = {
      organizationId,
      memberIds,
      role,
    };
    return this.http
      .patch<IMember[]>(url, data, { headers: this.headers })
      .pipe(catchError(this.errorMgmt));
  }

  bulkRemoveRole(
    organizationId: string,
    memberIds: string[],
    role: string
  ): Observable<IMember[]> {
    const url = this.urlService.buildUrl(
      this.collectionname,
      'bulk-remove-role'
    );
    const data = {
      organizationId,
      memberIds,
      role,
    };
    return this.http
      .patch<IMember[]>(url, data, { headers: this.headers })
      .pipe(catchError(this.errorMgmt));
  }
}
